import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { Seo } from "@/components/Seo";
import { colorFamily } from "@/lib/colors";
import { sizeValue } from "@/lib/sizes";
import { FilterBar, matchesPriceRanges } from "@/components/FilterBar";

const PAGE = 24;

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const collection = params.get("collection") || "shop-all";
  const max = params.get("max") ? Number(params.get("max")) : null;
  const deal = params.get("deal") === "1";
  const q = params.get("q") || "";
  const { addItem } = useCart();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("newest");
  const [visible, setVisible] = useState(PAGE);
  const [loading, setLoading] = useState(true);
  const [sizeFilters, setSizeFilters] = useState([]);
  const [colorFilters, setColorFilters] = useState([]);
  const [priceFilters, setPriceFilters] = useState([]);

  useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setSizeFilters([]);
    setColorFilters([]);
    setPriceFilters([]);
    const parts = [];
    if (q) parts.push(`q=${encodeURIComponent(q)}`);
    else if (collection && collection !== "shop-all") parts.push(`collection=${collection}`);
    else parts.push("limit=2000");
    api.get(`/products?${parts.join("&")}`)
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [collection, q]);

  useEffect(() => setVisible(PAGE), [collection, q, max, deal, sort, sizeFilters, colorFilters, priceFilters]);

  const active = collections.find((c) => c.slug === collection);

  const list = useMemo(() => {
    let l = [...products].filter((p) => p.slug !== "alteration-service");
    if (max) l = l.filter((p) => p.price <= max);
    if (deal) l = l.filter((p) => p.compare_at && p.compare_at > p.price);
    if (sizeFilters.length) l = l.filter((p) => (p.sizes || []).some((sz) => sizeFilters.includes(sizeValue(sz))));
    if (colorFilters.length) l = l.filter((p) => (p.colors || []).some((c) => colorFilters.includes(colorFamily(c))));
    if (priceFilters.length) l = l.filter((p) => matchesPriceRanges(p.price, priceFilters));
    if (sort === "low") l.sort((a, b) => a.price - b.price);
    if (sort === "high") l.sort((a, b) => b.price - a.price);
    if (sort === "discount") l.sort((a, b) => b.discount_pct - a.discount_pct);
    if (sort === "newest") l.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    else if (deal) l.sort((a, b) => b.discount_pct - a.discount_pct);
    return l;
  }, [products, max, deal, sort, sizeFilters, colorFilters, priceFilters]);

  const shown = list.slice(0, visible);
  const clearFilters = () => { setSizeFilters([]); setColorFilters([]); setPriceFilters([]); };

  const setCollection = (slug) => {
    const p = new URLSearchParams();
    if (slug !== "shop-all") p.set("collection", slug);
    setParams(p);
  };

  const heading = q ? `“${q}”` : deal ? "Deals & Sale" : max ? `Under $${max}` : active ? active.title : "Shop All";

  return (
    <div data-testid="shop-page" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10 min-h-[60vh]">
      <Seo
        title={q ? `Search: ${q}` : `${heading} — Indian Ethnic Wear`}
        description={`Shop ${heading !== "Shop All" ? heading.toLowerCase() : "Indian ethnic wear"} at Kurti Connection USA — kurtis, sarees, lehengas & party wear. In-store near Chicago, shipping across the USA.`}
        path={q ? "/shop" : collection !== "shop-all" ? `/shop?collection=${collection}` : "/shop"}
        noindex={!!q}
      />
      <div className="text-center mb-8">
        <div className="overline text-[#6E6E69]">{q ? "Search results" : deal ? "Up to 60% Off" : "Kurti Connection USA"}</div>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight mt-1" data-testid="shop-title">{heading}</h1>
        {deal && <p className="text-sm text-[#6E6E69] mt-2 max-w-md mx-auto">Every piece on sale right now — going fast.</p>}
        {active?.description && !max && !q && !deal && <p className="text-sm text-[#6E6E69] mt-2 max-w-md mx-auto">{active.description}</p>}
      </div>

      {!q && !deal && (
        <div className="flex gap-2.5 overflow-x-auto pb-4 mb-6 no-scrollbar border-b border-[#E5E5DF]" data-testid="shop-filters">
          {collections.map((c) => (
            <button key={c.slug} onClick={() => setCollection(c.slug)} data-testid={`filter-${c.slug}`}
              className={`flex-shrink-0 px-4 py-2 text-[0.68rem] font-mono tracking-[0.12em] uppercase border transition-colors duration-200 ${
                collection === c.slug && !max ? "bg-[#1A1A1A] text-[#FFFFFF] border-[#1A1A1A]" : "border-[#E5E5DF] text-[#1A1A1A] hover:border-[#1A1A1A]"
              }`}>{c.title}</button>
          ))}
        </div>
      )}

      <FilterBar products={products} sizes={sizeFilters} colors={colorFilters} prices={priceFilters}
        onSizes={setSizeFilters} onColors={setColorFilters} onPrices={setPriceFilters} />

      <div className="flex items-center justify-between mb-8">
        <span className="text-sm text-[#6E6E69] flex items-center gap-3" data-testid="shop-count">
          {list.length} {list.length === 1 ? "piece" : "pieces"}
          {q && <Link to="/shop" data-testid="clear-search" className="overline text-[#B84A3B] border-b border-[#B84A3B] pb-0.5">Clear search</Link>}
        </span>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="shop-sort"
          className="border border-[#E5E5DF] bg-transparent px-4 py-2 text-[0.72rem] font-mono tracking-wide uppercase focus:outline-none focus:border-[#1A1A1A]">
          <option value="newest">Newest First</option>
          <option value="featured">Featured</option>
          <option value="low">Price: Low to High</option>
          <option value="high">Price: High to Low</option>
          <option value="discount">Biggest Discount</option>
        </select>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10" data-testid="shop-skeleton">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse"><div className="aspect-[2/3] bg-[#EFEFEA]" /><div className="h-3 bg-[#EFEFEA] mt-3 w-3/4" /><div className="h-3 bg-[#EFEFEA] mt-2 w-1/3" /></div>
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-24">
          {(sizeFilters.length > 0 || colorFilters.length > 0 || priceFilters.length > 0) ? (
            <>
              <p className="font-display text-3xl text-[#6E6E69]">No pieces match these filters</p>
              <button onClick={clearFilters} data-testid="empty-clear-filters" className="overline border-b border-[#1A1A1A] pb-1 mt-4 inline-block hover:text-[#B84A3B] transition-colors">Clear filters</button>
            </>
          ) : (
            <>
              <p className="font-display text-3xl text-[#6E6E69]">{q ? `No matches for “${q}”` : "Nothing here yet"}</p>
              <Link to="/shop" className="overline border-b border-[#1A1A1A] pb-1 mt-4 inline-block hover:text-[#B84A3B] transition-colors">Browse all pieces</Link>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10">
            {shown.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onAdd={(pr, s) => addItem(pr, s)} />)}
          </div>
          {visible < list.length && (
            <div className="flex flex-col items-center gap-3 mt-14">
              <span className="overline text-[#6E6E69]">Showing {shown.length} of {list.length}</span>
              <button data-testid="load-more-button" onClick={() => setVisible((v) => v + PAGE)}
                className="bg-[#1A1A1A] text-[#FFFFFF] px-10 py-4 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#B84A3B] transition-colors duration-300">
                Load More
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
